import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  IconButton,
  Breadcrumbs,
  Link,
  Tabs,
  Tab,
  Autocomplete,
  TextField,
  CircularProgress,
  Card,
  CardContent,
  Dialog,
  Tooltip,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import {
  getExcelPreview,
  type IExcelPreview,
  type ISharePointFile,
  type ISnapshot,
} from "../../services/sharepointService";
import type { TranslationKeys } from "../../i18n";
import {
  getBusinessCaseById,
  permanentlyDeleteBusinessCase,
  updateBusinessCaseInBackground,
  type IDeletedProjectCheckResponse,
  type IMaterialSubmission,
} from "../../services/businessCaseService";
import BusinessCaseInfoGrid from "../../components/customGenericComponents/infoGrid/BusinessCaseInfoGrid";
import MaterialFormTable from "../../components/customGenericComponents/materialForm/MaterialFormTable";
import BusinessCaseVersions from "../../components/customGenericComponents/BusinessCaseVersions/BusinessCaseVersions";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { useBCT } from "../../hooks/useBCT";
import ProjectNameConfirmationModal from "../../components/customGenericComponents/modals/ProjectNameConfirmationModal ";
import DeletedProjectWarningModal from "../../components/customGenericComponents/modals/DeletedProjectWarningModal";

const TabPanel = ({
  children,
  value,
  index,
}: {
  children: React.ReactNode;
  value: number;
  index: number;
}) => (
  <div hidden={value !== index} role="tabpanel">
    {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
  </div>
);

const BusinessCaseOverview: React.FC = () => {
  const [snapshots, setSnapshots] = useState<ISnapshot[]>([]);
  const [materialTabIndex, setMaterialTabIndex] = useState(0);
  const [rows, setRows] = useState<{ new: IMaterialSubmission[]; old: IMaterialSubmission[] }>({
    new: [],
    old: [],
  });

  const { t } = useTranslation<TranslationKeys>();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<any[]>([]);
  const [businessCase, setBusinessCase] = useState<any>(null);
  const [_error, setError] = useState<string | null>(null);

  // SharePoint file management
  const { sharepointFiles, sharepointFilesLoading } = useBCT();
  const [selectedFile, setSelectedFile] = useState<ISharePointFile | null>(
    null
  );
  const [excelPreview, setExcelPreview] = useState<IExcelPreview | null>(null);
  const [updating, setUpdating] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [sharepointPreviewUrl, setSharepointPreviewUrl] = useState<string | null>(null);

  const [projectConfirmModal, setProjectConfirmModal] = useState<{
    open: boolean;
    originalProjectName: string;
    fileProjectName: string;
  }>({
    open: false,
    originalProjectName: '',
    fileProjectName: '',
  });

  const [deletedProjectCheck, setDeletedProjectCheck] = useState<{
    open: boolean;
    data: IDeletedProjectCheckResponse | null;
    processing: boolean;
  }>({
    open: false,
    data: null,
    processing: false,
  });

  const snapshotImages = React.useMemo(() => {
    let images: any[] = [];
    
    if (showPreview && excelPreview?.snapshots) {
      // Show preview snapshots from the new API call
      images = Object.entries(excelPreview.snapshots).map(([sheet_name, blob_url]) => ({
        sheet_name,
        blob_url,
        file_name: selectedFile?.name || "Preview File",
      }));
    } else {
      // Show default snapshots from the initial API call
      images = snapshots.map((s) => ({
        sheet_name: s.sheet_name,
        blob_url: s.blob_url,
        file_name: s.file_name,
      }));
    }
    
    // Filter to show only the required snapshots
    return images.filter(
      (s: any) =>
        s.blob_url.includes("_PROJECT_INPUTEqp.jpg") ||
        s.blob_url.includes("_SALES_INPUTEqp.jpg")
    );
  }, [showPreview, excelPreview, snapshots, selectedFile]);
  const handleOpen = (index: number) => {
    setCurrentIndex(index);
    setLightboxOpen(true);
  };

  const handleClose = () => {
    setLightboxOpen(false);
  };

  const handlePrev = () => {
    setCurrentIndex((prev) =>
      prev === 0 ? snapshotImages.length - 1 : prev - 1
    );
  };

  const handleNext = () => {
    setCurrentIndex((prev) =>
      prev === snapshotImages.length - 1 ? 0 : prev + 1
    );
  };

  useEffect(() => {
    if (id) {
      fetchBusinessCase(id);
    }
  }, [id]);

  const fetchBusinessCase = async (caseId: string) => {
    try {
      setIsLoading(true);
      const result = await getBusinessCaseById(caseId);
      setBusinessCase(result);
      setSnapshots(result.snapshots || []);
      
      const uniqueNotifications = (result.notifications || []).filter(
        (n, index, self) =>
          index === self.findIndex((t) => t.file_name === n.file_name)
      );
      
      setNotifications(uniqueNotifications);
      // setNotifications(result.notifications || []);

      // Populate material rows from existing assignments
      const materialAssignments =
        result.materials || [];

      const newMaterials = materialAssignments.filter(
        (mat: any) => mat.subNew === true
      );
      const substitutionMaterials = materialAssignments.filter(
        (mat: any) => mat.subNew === false
      );

      setRows({
        new: newMaterials.map((mat: any) => ({
          productFamily: mat.productFamily || "",
          productGroup: mat.productGroup || "",
          productSubGroup: mat.productSubGroup || "",
          material: mat.material || "",
        })),
        old: substitutionMaterials.map((mat: any) => ({
          productFamily: mat.productFamily || "",
          productGroup: mat.productGroup || "",
          productSubGroup: mat.productSubGroup || "",
          material: mat.material || "",
        })),
      });
    } catch (err: any) {
      setError(err?.response?.data?.error || "Failed to load business case.");
    } finally {
      setIsLoading(false);
    }
  };

  // Auto-select file when SharePoint files and business case data are loaded
  useEffect(() => {
    if (
      sharepointFiles.length > 0 &&
      businessCase?.excel_file_name &&
      !selectedFile
    ) {
      // Try to match the stored excel file name with SharePoint files
      const matchedFile = sharepointFiles.find(
        (file) => file.name === businessCase.excel_file_name
      );

      if (matchedFile) {
        setSelectedFile(matchedFile);
      } else {
        // If no exact match, create a placeholder with the stored name
        setSelectedFile({
          id: "stored",
          name: businessCase.excel_file_name,
          webUrl: "",
          lastModified:
            businessCase.last_updated_at || new Date().toISOString(),
          size: 0,
          contentType:
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
      }
    }
  }, [sharepointFiles, businessCase, selectedFile]);

  const handleBackClick = () => {
    navigate("/dashboard");
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const formatLastModified = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      return dateString;
    }
  };

  const handleMaterialTabChange = (
    _: React.SyntheticEvent,
    newValue: number
  ) => {
    setMaterialTabIndex(newValue);
  };

  const handleFileChange = (newValue: ISharePointFile | null) => {
    // Simply set the file without validation
    setSelectedFile(newValue);
    setError(null);
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100%",
        }}
      >
        <CircularProgress size={50} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          {t("loading")}
        </Typography>
      </Box>
    );
  }

  if (!businessCase) {
    return (
      <Box sx={{ px: 2, py: 3 }}>
        <Typography>{t("no_data_available")}</Typography>
      </Box>
    );
  }

  const handleSaveAndClose = async () => {
    // Check if we have any changes to save
    const hasFileChange = selectedFile && selectedFile.name !== businessCase?.excel_file_name;
    const hasMaterialChanges = rows.new.length > 0 || rows.old.length > 0;

    const originallyHadMaterials = businessCase?.materials && businessCase.materials.length > 0;
    const currentlyHasNoMaterials = rows.new.length === 0 && rows.old.length === 0;
    const materialsDeleted = originallyHadMaterials && currentlyHasNoMaterials;
    
    if (!hasFileChange && !hasMaterialChanges && !materialsDeleted) {
      toast.error(t("warn_no_changes_to_save"));
      return;
    }

    setUpdating(true);

    // Perform update immediately in background
    await performUpdate();
  };

const performUpdate = async (
  projectNameChoice?: 'original' | 'new', 
  deletedProjectAlreadyChecked?: IDeletedProjectCheckResponse
) => {
  try {
    const hasFileChange = selectedFile && selectedFile.name !== businessCase?.excel_file_name;
    
    // Show appropriate toast based on project name choice
    if (projectNameChoice === 'original') {
      toast.info(t('toast.keep_current_project_name'), {
        autoClose: 5000,
      });
    } else if (projectNameChoice === 'new') {
      toast.info(t('toast.change_to_new_project_name'), {
        autoClose: 5000,
      });
    }
    
    toast.info(t('toast.updating_in_background'), {
      autoClose: 8000,
    });
    
    // Execute in background with progress tracking
    await updateBusinessCaseInBackground(
      id!,
      businessCase?.project_name || 'Business Case',
      hasFileChange ? selectedFile : null,
      rows,
      projectNameChoice,
      (progress) => {
        // Live progress updates
        if (progress.status === 'completed') {
          const result = progress.result;
          const updateType = result?.type || 'unknown';
          const projectName = businessCase?.project_name || 'Business Case';
          
          switch (updateType) {
            case 'version_update':
              toast.success(t('toast.updated_version_successfully', { 
                name: projectName, 
                version: result.version_number 
              }), {
                autoClose: 6000,
              });
              break;
            case 'material_update':
              toast.success(t('toast.updated_materials_successfully', { name: projectName }), {
                autoClose: 6000,
              });
              break;
            case 'no_changes':
              toast.info(t('toast.no_changes_info', { name: projectName }), {
                autoClose: 4000,
              });
              break;
            default:
              toast.success(t('toast.updated_successfully', { name: projectName }), {
                autoClose: 6000,
              });
          }
          
          setUpdating(false); // Re-enable button on success
          
          // Refresh data without navigation
          window.dispatchEvent(new CustomEvent('business-case-updated'));
        } else if (progress.status === 'failed' || progress.status === 'timeout') {
          // Only show toast if it's NOT a user cancellation (those are handled separately)
          if (!progress.error?.includes('cancelled') && !progress.error?.includes('User cancelled')) {
            toast.error(progress.error || t('toast.update_failed'));
          }
          setUpdating(false); // Re-enable button on error
        }
      },
      deletedProjectAlreadyChecked
    );
    
  } catch (error: any) {
    // Check if it's a user cancellation - show info toast only once
    if (error.name === "AbortError" || error.message?.includes("cancelled") || error.message?.includes("User cancelled")) {
      toast.info(t('toast.update_cancelled'));
      setUpdating(false);
      return;
    }
    
    const errorMessage =
      error?.response?.data?.error || error.message || t('toast.update_failed');
    toast.error(errorMessage);
    setError(errorMessage);
    console.error("Error updating business case:", error);
    setUpdating(false);
  }
};

  // NEW: Handle project name confirmation choices
  const handleProjectNameChoice = async (choice: 'original' | 'new') => {
    try {
      setProjectConfirmModal(prev => ({ ...prev, open: false }));
      setUpdating(true);
      await performUpdate(choice);
    } catch (error: any) {
      // Check if it's a user cancellation - already handled in performUpdate
      if (error.name === "AbortError" || error.message?.includes("cancelled") || error.message?.includes("User cancelled")) {
        return;
      }
      const errorMessage =
        error?.response?.data?.error || error.message || t('toast.update_failed');
      toast.error(errorMessage);
      setError(errorMessage);
      console.error("Error updating business case:", error);
      setUpdating(false);
    }
  };

  const handleCancelProjectChoice = () => {
    setProjectConfirmModal(prev => ({ ...prev, open: false }));
    setSelectedFile(null); // Clear file selection
    toast.info(t('toast.select_correct_file'));
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "Not set";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const handleDeletedProjectConfirm = async () => {
    if (!deletedProjectCheck.data?.bc_id || !selectedFile) return;

    try {
      setDeletedProjectCheck(prev => ({ ...prev, processing: true }));
      
      // Permanently delete the old deleted business case
      await permanentlyDeleteBusinessCase(deletedProjectCheck.data.bc_id.toString());

      toast.success(t('previous_business_case_deleted_successfully'));
      
      const deletedData = deletedProjectCheck.data;
      
      // Close the deleted project modal
      setDeletedProjectCheck({
        open: false,
        data: null,
        processing: false,
      });

      // Proceed with update, passing deleted project data to skip re-checking
      await performUpdate(undefined, deletedData);

    } catch (error: any) {
      // Check if it's a user cancellation - already handled in performUpdate
      if (error.name === "AbortError" || error.message?.includes("cancelled") || error.message?.includes("User cancelled")) {
        setDeletedProjectCheck(prev => ({ ...prev, processing: false }));
        return;
      }
      const errorMessage = error?.response?.data?.error || t('toast.failed_delete_previous_bc');
      toast.error(errorMessage);
      setDeletedProjectCheck(prev => ({ ...prev, processing: false }));
      setUpdating(false);
    }
  };

// Handler for deleted project cancellation
const handleDeletedProjectCancel = () => {
  setDeletedProjectCheck({
    open: false,
    data: null,
    processing: false,
  });
  setUpdating(false);
  setSelectedFile(null); // Clear file selection
  toast.info(t('update_cancelled_select_different_file'));
};

  if (!businessCase) {
    return (
      <Box sx={{ px: 2, py: 3 }}>
        <Typography>{t("no_data_available")}</Typography>
      </Box>
    );
  }

  // Prepare info grid data with all requested fields
  const infoGridData = [
    { label: t("business_case_name"), value: businessCase.bc_name || "N/A" },
    { label: t("project_name"), value: businessCase.project_name || "N/A" },
    { label: t("project_gpm"), value: businessCase.gpm || "N/A" },
    { label: t("business_unit"), value: businessCase.bu?.name || "N/A" },
    { label: t("created_by"), value: businessCase.created_by_name || "N/A" },
    { label: t("created_at"), value: formatDate(businessCase.created_at) },
    // Additional fields from latest version
    { label: t("business_case_owner"), value: businessCase.bc_owner || "N/A" },
    { label: t("io_code"), value: businessCase.io_code || "N/A" },
    { label: t("column.cpr_date"), value: formatDate(businessCase.cpr_date) },
    {
      label: t("project_start_date"),
      value: formatDate(businessCase.project_start_date),
    },
    {
      label: t("revenue_start_date"),
      value: formatDate(businessCase.revenue_start_date),
    },
    {
      label: t("last_updated_at"),
      value: formatDate(businessCase.last_updated_at),
    },
    {
      label: t("last_updated_by"),
      value: businessCase.last_updated_by_name || "N/A",
    },
  ];

  // Get gates from latest version
  const gates = businessCase.latest_version?.gates || [];


  // Handlers for material rows changes (added for the new container)
  const handleNewMaterialRowsChange = (updatedRows: IMaterialSubmission[]) => {
    setRows((prev) => ({ ...prev, new: updatedRows }));
  };

  const handleSubstitutionMaterialRowsChange = (updatedRows: IMaterialSubmission[]) => {
    setRows((prev) => ({ ...prev, old: updatedRows }));
  };

  const handlePreviewClick = async () => {
    if(!selectedFile) {
      toast.error(t("warn_select_file_for_preview"));
      return;
    }
    
    setPreviewLoading(true);
    setShowPreview(false);
    setSharepointPreviewUrl(null);
    setExcelPreview(null);
    
    try {
      const data = await getExcelPreview(selectedFile);
      const snapshots = data?.data?.snapshots;
      const previewUrl = data?.data?.sharepoint_preview_url;

      if (snapshots && Object.keys(snapshots).length > 0) {
        // Show snapshots
        setExcelPreview(data.data);
        setShowPreview(true);
      } else {
        // Show SharePoint button
        setSharepointPreviewUrl(previewUrl || selectedFile.webUrl);
        setShowPreview(true);
      }
    } catch (error) {
      // Always fallback to SharePoint URL on any error
      console.log("Using SharePoint preview as fallback");
      setSharepointPreviewUrl(selectedFile.webUrl);
      setShowPreview(true);
    } finally {
      setPreviewLoading(false);
    }
  };

  return (
    <Box sx={{ px: 2, py: 3, backgroundColor: "#f5f7fa" }}>
      <Box sx={{ mb: 2 }}>
        <Breadcrumbs aria-label="breadcrumb">
          <Link
            underline="hover"
            fontSize="medium"
            component="button"
            variant="body2"
            onClick={handleBackClick}
            sx={{ textDecoration: "none", color: "text.secondary" }}
          >
            {t("dashboard")}
          </Link>
          <Link
            underline="hover"
            fontSize="medium"
            component="button"
            variant="body2"
            onClick={() => window.location.reload()} 
            sx={{ textDecoration: "none", color: "text.primary" }}
          >
            {businessCase?.bc_name ? `${businessCase.bc_name} Overview` : t("business_case_overview")}
          </Link>
        </Breadcrumbs>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <IconButton
          onClick={handleBackClick}
          sx={{
            mr: 2,
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" fontWeight={600}>
          {businessCase.bc_name || "Business Case Overview"}
        </Typography>
      </Box>

      {/* Card 1: Business Case Information */}
      <Paper
        elevation={1}
        sx={{
          p: 3,
          mb: 2,
          borderRadius: 3,
          backgroundColor: "#ffffff",
          border: "1px solid #e0e0e0",
        }}
      >
        {notifications.length > 0 && (
          <Paper
            elevation={1}
            sx={{
              p: 1,
              mb: 1,
              borderRadius: 1,
              display: "flex",
              alignItems: "flex-start",
              gap: 1,
              backgroundColor: "#fdecea",
              borderLeft: "6px solid #c4262e",
            }}
          >
            <Box>
              <Typography
                variant="subtitle1"
                fontWeight={600}
                sx={{ color: "#c4262e" }}
              >
                📂 {t('new_files_uploaded')}
              </Typography>
              <ul
                style={{
                  marginTop: "4px",
                  marginBottom: 0,
                  paddingLeft: "20px",
                }}
              >
                {notifications.map((n) => (
                  <li key={n.id} style={{ marginBottom: "2px" }}>
                    <Typography variant="body2" color="text.secondary">
                      {n.file_name}
                    </Typography>
                  </li>
                ))}
              </ul>
            </Box>
          </Paper>
        )}

        {/* Card 1: Business Case Information and Input Snapshots */}
        <Paper
          elevation={1}
          sx={{
            p: 3,
            mb: 2,
            borderRadius: 3,
            backgroundColor: "#ffffff",
            border: "1px solid #e0e0e0",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 3,
              mb: 2,
              backgroundColor: "white",
            }}
          >
            {/* Left Section: All Business Case Information (68%) */}
            <Box sx={{ flex: { xs: "1 1 100%", md: "0 0 68%" } }}>
              <Typography
                variant="h6"
                sx={{ mb: 3, fontWeight: 600, color: "#e04a50" }}
              >
                {t('business_case_information')}
              </Typography>
              
              {/* Organized Business Case Info Grid */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                {/* Row 1: Basic Information & Ownership & Codes side by side */}
                <Box sx={{ display: "flex", gap: 3 }}>
                  {/* Section 1: Basic Information */}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: "#666" }}>
                      Basic Information
                    </Typography>
                    <Grid container spacing={3}>
                      <Grid item xs={6}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            {t("business_case_name")}
                          </Typography>
                          <Typography variant="body1" fontWeight={500}>
                            {businessCase.bc_name || "N/A"}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            {t("project_name")}
                          </Typography>
                          <Typography variant="body1" fontWeight={500}>
                            {businessCase.project_name || "N/A"}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            {t("project_gpm")}
                          </Typography>
                          <Typography variant="body1" fontWeight={500}>
                            {businessCase.gpm || "N/A"}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            {t("business_unit")}
                          </Typography>
                          <Typography variant="body1" fontWeight={500}>
                            {businessCase.bu?.name || "N/A"}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Section 2: Ownership & Codes */}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: "#666" }}>
                      Ownership & Codes
                    </Typography>
                    <Grid container spacing={3}>
                      <Grid item xs={6}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            {t("business_case_owner")}
                          </Typography>
                          <Typography variant="body1" fontWeight={500}>
                            {businessCase.bc_owner || "N/A"}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            {t("io_code")}
                          </Typography>
                          <Typography variant="body1" fontWeight={500}>
                            {businessCase.io_code || "N/A"}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </Box>

                {/* Row 2: Important Dates & Audit Information side by side */}
                <Box sx={{ display: "flex", gap: 3 }}>
                  {/* Section 3: Important Dates */}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: "#666" }}>
                      Important Dates
                    </Typography>
                    <Grid container spacing={3}>
                      <Grid item xs={6}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            {t("column.cpr_date")}
                          </Typography>
                          <Typography variant="body1" fontWeight={500}>
                            {formatDate(businessCase.cpr_date)}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            {t("project_start_date")}
                          </Typography>
                          <Typography variant="body1" fontWeight={500}>
                            {formatDate(businessCase.project_start_date)}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            {t("revenue_start_date")}
                          </Typography>
                          <Typography variant="body1" fontWeight={500}>
                            {formatDate(businessCase.revenue_start_date)}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Section 4: Audit Information */}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: "#666" }}>
                      Audit Information
                    </Typography>
                    <Grid container spacing={3}>
                      <Grid item xs={6}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            {t("created_by")}
                          </Typography>
                          <Typography variant="body1" fontWeight={500}>
                            {businessCase.created_by_name || "N/A"}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            {t("created_at")}
                          </Typography>
                          <Typography variant="body1" fontWeight={500}>
                            {formatDate(businessCase.created_at)}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            {t("last_updated_by")}
                          </Typography>
                          <Typography variant="body1" fontWeight={500}>
                            {businessCase.last_updated_by_name || "N/A"}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            {t("last_updated_at")}
                          </Typography>
                          <Typography variant="body1" fontWeight={500}>
                            {formatDate(businessCase.last_updated_at)}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </Box>

                {/* Section 5: File Information with Selection */}
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: "#666" }}>
                    File Information
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                    <Box sx={{ flex: 1 }}>
                      <Autocomplete
                        options={sharepointFiles}
                        getOptionLabel={(option) => option.name}
                        value={selectedFile}
                        onChange={(_, newValue) => handleFileChange(newValue)}
                        loading={sharepointFilesLoading}
                        size="small"
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder={t("select_excel_file")}
                            variant="outlined"
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <React.Fragment>
                                  {sharepointFilesLoading ? (
                                    <CircularProgress color="inherit" size={20} />
                                  ) : null}
                                  {params.InputProps.endAdornment}
                                </React.Fragment>
                              ),
                            }}
                          />
                        )}
                        renderOption={(props, option) => (
                          <li {...props} key={option.id}>
                            <Box sx={{ width: "100%" }}>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {option.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Size: {formatFileSize(option.size)} • Modified: {formatLastModified(option.lastModified)}
                              </Typography>
                            </Box>
                          </li>
                        )}
                        filterOptions={(options, { inputValue }) =>
                          options.filter((option) =>
                            option.name
                              .toLowerCase()
                              .includes(inputValue.toLowerCase())
                          )
                        }
                        noOptionsText={("no_files_found")}
                        isOptionEqualToValue={(option, value) =>
                          option.id === value.id
                        }
                      />
                    </Box>
                    <Tooltip title={t("preview")}>
                      <span>
                          <Button
                            onClick={handlePreviewClick}
                            disabled={!selectedFile}
                            sx={{
                              backgroundColor: "#c4262e",
                              color: "white",
                              textTransform: "none",
                              fontSize: "0.875rem",
                              minWidth: "90px",
                              height: "40px",
                              "&:hover": {
                                backgroundColor: "#a01e24",
                              },
                              "&:disabled": {
                                backgroundColor: "#f5c6c3",
                                color: "#fff",
                              },
                            }}
                          >
                            {t('preview')}
                          </Button>
                      </span>
                    </Tooltip>
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* Right Section: Preview Container (32%) */}
            <Box sx={{ flex: { xs: "1 1 100%", md: "0 0 32%" }, display: "flex", flexDirection: "column", gap: 3, pr: { xs: 0, md: 2 } }}>
              {/* Preview Section */}
              <Box
                sx={{
                  minHeight: 200,
                  maxHeight: 280,
                  border: "2px dashed #e0e0e0",
                  borderRadius: 2,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  p: 2,
                  backgroundColor: "#fafafa",
                }}
              >
                {previewLoading ? (
                  <Box sx={{ textAlign: "center" }}>
                    <CircularProgress size={40} sx={{ mb: 2 }} />
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      {t("loading_preview_please_wait")}
                    </Typography>
                  </Box>
                ) : sharepointPreviewUrl ? (
                  <Box sx={{ textAlign: "center" }}>
                    <Typography variant="h4" sx={{ mb: 2 }}>
                      {t("snapshot_not_available")}
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      href={sharepointPreviewUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ textTransform: "none" }}
                    >
                      {t("view_in_sharepoint")}
                    </Button>
                  </Box>
                ) : selectedFile && !showPreview ? (
                  <Box sx={{ textAlign: "center" }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      {t("file_details")} :
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong> {t("size")} :</strong>{" "}
                      {formatFileSize(selectedFile.size)}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>{t("last_modified")} :</strong>{" "}
                      {formatLastModified(selectedFile.lastModified)}
                    </Typography>
                    <Typography variant="body2">
                      <strong> Last Modified By:</strong>{" "}
                      {selectedFile.lastModifiedBy || "N/A"}
                    </Typography>
                  </Box>
                ) : showPreview && snapshotImages.length > 0 ? (
                  <Box sx={{ width: "100%", overflowY: "auto", maxHeight: 250 }}>
                    <Typography variant="h6" sx={{ mb: 2, textAlign: "center" }}>
                      {t("preview_snapshots")}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", justifyContent: "center" }}>
                      {snapshotImages.slice(0, 2).map((snapshot, index) => (
                        <Card
                          key={index}
                          sx={{
                            width: 140,
                            height: 100,
                            cursor: "pointer",
                            display: "flex",
                            flexDirection: "column",
                            "&:hover": { boxShadow: 3 },
                          }}
                          onClick={() =>
                            handleOpen(
                              snapshotImages.findIndex(
                                (s) => s.blob_url === snapshot.blob_url
                              )
                            )
                          }
                        >
                          <Box
                            component="img"
                            src={snapshot.blob_url}
                            alt={snapshot.file_name}
                            sx={{
                              width: "100%",
                              height: 70,
                              objectFit: "contain",
                              borderBottom: "1px solid #eee",
                            }}
                          />

                          <CardContent sx={{ p: 0.5, textAlign: "center" }}>
                            <Typography
                              variant="caption"
                              sx={{ fontSize: "12px" }}
                              noWrap
                            >
                              {snapshot.sheet_name.includes(
                                "PROJECT_INPUT(Eqp)"
                              )
                                ? "Project_Input(Eqp)"
                                : "Sales_Input(Eqp)"}
                            </Typography>
                          </CardContent>
                        </Card>
                      ))}
                    </Box>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center" }}>
                    {!selectedFile 
                      ? t("select_excel_file")
                      : t('no_snapshots_found')
                    }
                  </Typography>
                )}
              </Box>

              {/* Quick Summary Section */}
              <Box
                sx={{
                  border: "1px solid #e0e0e0",
                  borderRadius: 2,
                  p: 2,
                  backgroundColor: "#ffffff",
                }}
              >
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: "#666" }}>
                  Quick Summary
                </Typography>
                <Box sx={{ display: "flex", justifyContent: "space-around", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                  <Box sx={{ textAlign: "center", flex: "1 1 auto", minWidth: "80px" }}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                      Total Versions
                    </Typography>
                    <Typography variant="h4" fontWeight={600}>
                      {businessCase?.versions?.length || 0}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: "center", flex: "1 1 auto", minWidth: "80px" }}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                      New Materials
                    </Typography>
                    <Typography variant="h4" fontWeight={600}>
                      {rows.new.length || 0}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: "center", flex: "1 1 auto", minWidth: "80px" }}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                      Substitution Materials
                    </Typography>
                    <Typography variant="h4" fontWeight={600}>
                      {rows.old.length || 0}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* Lightbox Dialog */}
            <Dialog
              open={lightboxOpen}
              onClose={handleClose}
              fullWidth
              maxWidth="xl"
              PaperProps={{
                sx: { position: "relative", bgcolor: "black" },
              }}
            >
              {/* Close Button */}
              <IconButton
                onClick={handleClose}
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  color: "red",
                }}
              >
                <CloseIcon />
              </IconButton>

              {/* Prev Button */}
              <IconButton
                onClick={handlePrev}
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: 8,
                  color: "red",
                }}
              >
                <ArrowBackIosNewIcon />
              </IconButton>

              {/* Next Button */}
              <IconButton
                onClick={handleNext}
                sx={{
                  position: "absolute",
                  top: "50%",
                  right: 8,
                  color: "red",
                }}
              >
                <ArrowForwardIosIcon />
              </IconButton>

              {/* Image */}
              <Box
                component="img"
                src={snapshotImages[currentIndex]?.blob_url}
                alt={snapshotImages[currentIndex]?.file_name}
                sx={{
                  width: "100%",
                  maxHeight: "80vh",
                  objectFit: "contain",
                }}
              />
            </Dialog>
          </Box>
        </Paper>

        {/* Card 2: Gate Dates */}
        <Paper
          elevation={1}
          sx={{
            p: 3,
            mb: 2,
            borderRadius: 3,
            backgroundColor: "#ffffff",
            border: "1px solid #e0e0e0",
          }}
        >
          <Typography
            variant="h6"
            sx={{ mb: 3, fontWeight: 600, color: "#e04a50" }}
          >
            {t('gate_dates')}
          </Typography>

          {gates.length > 0 ? (
            <Grid container spacing={2}>
              {gates.map((gate: any, index: number) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Card sx={{ p: 2 }}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {gate.label}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(gate.date)}
                    </Typography>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Typography variant="body2" color="text.secondary">
             {t("no_gates_found")}
            </Typography>
          )}
        </Paper>

        {/* Card 3: Business Case Versions */}
        <Paper
          elevation={1}
          sx={{
            p: 3,
            mb: 2,
            borderRadius: 3,
            backgroundColor: "#ffffff",
            border: "1px solid #e0e0e0",
          }}
        >
          <Typography
            variant="h6"
            sx={{ mb: 3, fontWeight: 600, color: "#e04a50" }}
          >
            Business Case Versions
          </Typography>

          {businessCase?.versions && businessCase.versions.length > 0 ? (
            <BusinessCaseVersions
              versions={businessCase.versions.map((v: any) => ({
                id: v.id,
                version: v.version,
                bc_file_name: v.bc_file_name,
                last_updated_at: v.last_updated_at,
                last_updated_by_name: v.last_updated_by_name,
              }))}
              businessCaseId={id || ""}
              onVersionUpdate={() => fetchBusinessCase(id || "")}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              No versions found
            </Typography>
          )}
        </Paper>
      </Paper>

      {/* Card 4: Material Submissions */}
      <Paper
        elevation={1}
        sx={{
          p: 3,
          mb: 2,
          borderRadius: 3,
          backgroundColor: "#ffffff",
          border: "1px solid #e0e0e0",
        }}
      >
        <Typography
          variant="h6"
          sx={{ mb: 3, fontWeight: 600, color: "#e04a50" }}
        >
          {t('material_submissions')}
        </Typography>

        <Tabs
          value={materialTabIndex}
          onChange={handleMaterialTabChange}
          textColor="primary"
          indicatorColor="primary"
        >
          <Tab label={t("new_materials")} />
          <Tab label={t("substitution_materials")} />
        </Tabs>

        <TabPanel value={materialTabIndex} index={0}>
          <MaterialFormTable
            rows={rows.new}
            onChange={handleNewMaterialRowsChange}
          />
        </TabPanel>

        <TabPanel value={materialTabIndex} index={1}>
          <MaterialFormTable
            rows={rows.old}
            onChange={handleSubstitutionMaterialRowsChange}
          />
        </TabPanel>
      </Paper>

      {/* Save & Close Button */}
      <Box display="flex" justifyContent="flex-end" mt={3}>
        <Button
          size="medium"
          variant="contained"
          onClick={handleSaveAndClose}
          disabled={updating}
          startIcon={updating ? <CircularProgress size={20} /> : undefined}
          sx={{
            backgroundColor: "#c4262e",
            textTransform: "none",
            "&:hover": { backgroundColor: "#a01e24" },
          }}
        >
          {updating ? t("updating") : t("update")}
        </Button>
      </Box>
      <DeletedProjectWarningModal
        open={deletedProjectCheck.open}
        projectName={deletedProjectCheck.data?.project_name || ''}
        deletedAt={deletedProjectCheck.data?.deleted_at || ''}
        deletedByName={deletedProjectCheck.data?.deleted_by_name || ''}
        loading={deletedProjectCheck.processing}
        onConfirm={handleDeletedProjectConfirm}
        onCancel={handleDeletedProjectCancel}
      />
      <ProjectNameConfirmationModal
        open={projectConfirmModal.open}
        originalProjectName={projectConfirmModal.originalProjectName}
        fileProjectName={projectConfirmModal.fileProjectName}
        onClose={handleCancelProjectChoice}
        onChooseOriginal={() => handleProjectNameChoice('original')}
        onChooseNew={() => handleProjectNameChoice('new')}
        loading={updating}
      />
    </Box>
  );
};

export default BusinessCaseOverview;
